"use client";

import { Building2 } from "lucide-react";

import { useDiscount } from "~/hooks/use-discount";
import { useOrganisation } from "~/hooks/use-organisation";
import { EUR } from "~/lib/currency";

interface ServicePriceProps {
  price: number;
}

/**
 * Same money-correctness rules as `TherapistPriceLabel` /
 * `TherapistBookingButtons`: sponsored-organisation members never see a
 * price, everyone else sees the discounted price struck through against the
 * full one when an active discount code applies.
 */
export function ServicePrice({ price }: ServicePriceProps) {
  const { shouldHidePrice, organisationName } = useOrganisation();
  const { hasDiscount, calculateDiscountedPrice } = useDiscount();

  if (shouldHidePrice) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground">
        <Building2 className="size-3.5" aria-hidden="true" />
        Pokriveno od {organisationName}
      </span>
    );
  }

  if (hasDiscount) {
    return (
      <span className="shrink-0 text-right">
        <span className="mr-1.5 text-xs text-muted-foreground line-through">
          {EUR.format(price)}
        </span>
        <span className="text-brand text-base font-semibold tabular-nums">
          {EUR.format(calculateDiscountedPrice(price))}
        </span>
      </span>
    );
  }

  return (
    <span className="shrink-0 text-base font-semibold tabular-nums">
      {EUR.format(price)}
    </span>
  );
}

/** Suspense fallback: the plain price, before organisation/discount state resolves. */
export function ServicePriceFallback({ price }: ServicePriceProps) {
  return (
    <span className="shrink-0 text-base font-semibold tabular-nums">
      {EUR.format(price)}
    </span>
  );
}
